import React from 'react';
import styled from 'styled-components';
import * as Diff from 'diff';

const DiffViewerContainer = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 1.5rem;
`;

const FieldName = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 0.75rem 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  text-transform: capitalize;
`;

const ComparisonContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  background: rgba(255, 255, 255, 0.1);
`;

const Column = styled.div`
  background: rgba(0, 0, 0, 0.2);
  padding: 1rem;
  overflow-x: auto;
`;

const ColumnHeader = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  padding: 0.5rem;
  border-radius: 4px;
  background: ${props => props.type === 'old' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)'};
  color: ${props => props.type === 'old' ? '#FCA5A5' : '#6EE7B7'};
  text-align: center;
`;

const ContentBlock = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  white-space: pre-wrap;
  word-break: break-word;
  padding: 0.5rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
`;

const DiffLine = styled.div`
  padding: 2px 4px;
  background: ${props => {
    if (props.type === 'added') return 'rgba(16, 185, 129, 0.2)';
    if (props.type === 'removed') return 'rgba(239, 68, 68, 0.2)';
    return 'transparent';
  }};
  color: ${props => {
    if (props.type === 'added') return '#6EE7B7';
    if (props.type === 'removed') return '#FCA5A5';
    return 'rgba(255, 255, 255, 0.8)';
  }};
  border-left: 3px solid ${props => {
    if (props.type === 'added') return '#10B981';
    if (props.type === 'removed') return '#EF4444';
    return 'transparent';
  }};
  margin: 1px 0;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

const NoChangeIndicator = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
  padding: 0.5rem;
  text-align: center;
`;

const SingleValueContainer = styled.div`
  background: rgba(0, 0, 0, 0.2);
  padding: 1rem;
`;

const ContentDiffViewer = ({ fieldName, oldValue, newValue, isNew = false }) => {
  // Handle null or undefined values
  const formatValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const oldStr = formatValue(oldValue);
  const newStr = formatValue(newValue);

  // Check if there's a difference
  const hasChanges = oldStr !== newStr;

  // For new content (no old version), show only the new value
  if (isNew) {
    return (
      <DiffViewerContainer>
        <FieldName>{fieldName.replace(/_/g, ' ')}</FieldName>
        <SingleValueContainer>
          <ColumnHeader type="new">New Content</ColumnHeader>
          <ContentBlock>{newStr || <NoChangeIndicator>No content</NoChangeIndicator>}</ContentBlock>
        </SingleValueContainer>
      </DiffViewerContainer>
    );
  }

  // For updates, show side-by-side comparison if there are changes
  if (hasChanges) {
    // Generate inline diff for better visualization
    const diff = Diff.diffWords(oldStr, newStr);
    
    return (
      <DiffViewerContainer>
        <FieldName>{fieldName.replace(/_/g, ' ')}</FieldName>
        <ComparisonContainer>
          <Column>
            <ColumnHeader type="old">Current (Approved)</ColumnHeader>
            <ContentBlock>{oldStr || <NoChangeIndicator>No content</NoChangeIndicator>}</ContentBlock>
          </Column>
          <Column>
            <ColumnHeader type="new">Proposed (Pending)</ColumnHeader>
            <div>
              {diff.map((part, index) => (
                <DiffLine
                  key={index}
                  type={part.added ? 'added' : part.removed ? 'removed' : 'unchanged'}
                >
                  {part.value}
                </DiffLine>
              ))}
            </div>
          </Column>
        </ComparisonContainer>
      </DiffViewerContainer>
    );
  }

  // No changes - show single view
  return (
    <DiffViewerContainer>
      <FieldName>{fieldName.replace(/_/g, ' ')}</FieldName>
      <SingleValueContainer>
        <NoChangeIndicator>No changes</NoChangeIndicator>
        <ContentBlock>{newStr || <NoChangeIndicator>No content</NoChangeIndicator>}</ContentBlock>
      </SingleValueContainer>
    </DiffViewerContainer>
  );
};

export default ContentDiffViewer;

